import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { API_URL } from '../config';
import {
  FaHeartbeat, FaExclamationTriangle, FaTachometerAlt,
  FaShieldAlt, FaCheckCircle, FaSync, FaToggleOn, FaToggleOff
} from 'react-icons/fa';

// ============================================================
// SYSTEM MANAGEMENT (Item 11) — Super Admin
// Shows ERRORS + SECURITY + HEALTH + PERFORMANCE.
// NOTE: the "Audit Log" (what admins did) is a SEPARATE thing and
// stays as its own sidebar item — see AuditLogPage.js.
// ============================================================

const TABS = [
  { id: 'overview', label: 'Overview', icon: FaHeartbeat },
  { id: 'errors', label: 'Errors', icon: FaExclamationTriangle },
  { id: 'performance', label: 'Performance', icon: FaTachometerAlt },
  { id: 'security', label: 'Security', icon: FaShieldAlt }
];

const SEVERITY = {
  critical: { color: '#DC2626', bg: '#FEE2E2', label: 'Critical' },
  high: { color: '#EA580C', bg: '#FFEDD5', label: 'High' },
  medium: { color: '#D97706', bg: '#FEF3C7', label: 'Medium' },
  low: { color: '#65A30D', bg: '#ECFCCB', label: 'Low' }
};

const STATUS = {
  open: { color: '#DC2626', bg: '#FEE2E2', label: 'Open' },
  investigating: { color: '#D97706', bg: '#FEF3C7', label: 'Investigating' },
  resolved: { color: '#16A34A', bg: '#DCFCE7', label: 'Resolved' }
};

const EVENT_TYPES = {
  error: 'Application error',
  failed_login: 'Failed login',
  rate_limit: 'Rate limit hit',
  cross_tenant: 'Cross-tenant attempt',
  system: 'System event'
};

const fmtTime = (iso) => new Date(iso).toLocaleString('en-ZA', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' });

function SystemManagement({ onLogout }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [tab, setTab] = useState('overview');
  const [health, setHealth] = useState(null);
  const [maintenance, setMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [healthRes, maintRes] = await Promise.all([
        fetch(`${API_URL}/admin/system-health`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/admin/system-health/maintenance`, { headers }).then(r => r.json())
      ]);

      if (healthRes.success) setHealth(healthRes);
      if (maintRes.success) setMaintenance(maintRes.maintenance_mode === true);
    } catch (e) {
      console.error('System management load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleMaintenance = async () => {
    const next = !maintenance;
    setMaintenance(next);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/admin/system-health/maintenance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: next })
      });
    } catch (e) {
      console.error('toggle maintenance error', e);
    }
  };

  const updateEventStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/admin/system-health/events/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      fetchData();
    } catch (e) {
      console.error('update status error', e);
    }
  };

  const events = health?.events || [];

  // ---- Derived metrics ----
  const errorEvents = events.filter(e => e.event_type === 'error');
  const securityEvents = events.filter(e => ['failed_login', 'rate_limit', 'cross_tenant'].includes(e.event_type));
  const openCount = events.filter(e => e.status !== 'resolved').length;

  const errorGroups = groupBy(errorEvents, 'message');
  const secGroups = groupBy(securityEvents, 'event_type');

  if (loading) {
    return <AdminLayout activeTab="system" onLogout={onLogout} user={user}>
      <div className="flex items-center justify-center h-64 text-gray-400">Loading system status…</div>
    </AdminLayout>;
  }

  return (
    <AdminLayout activeTab="system" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy">🛠 System Management</h1>
          <p className="text-sm text-gray-500">Health, errors, security and performance — one place for IT oversight.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchData()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-green hover:text-green transition"
          >
            <FaSync /> Refresh
          </button>
          <button
            onClick={toggleMaintenance}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${maintenance ? 'bg-red-600 text-white' : 'bg-white border border-gray-200 text-navy'}`}
          >
            {maintenance ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
            {maintenance ? 'Maintenance Mode ON' : 'Maintenance Mode OFF'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${active ? 'border-green text-navy' : 'border-transparent text-gray-500 hover:text-navy'}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ----- CONTENTS ----- */}

      {tab === 'overview' && <Overview events={events} errorEvents={errorEvents} securityEvents={securityEvents} openCount={openCount} maintenance={maintenance} health={health} />}

      {tab === 'errors' && <Errors groups={errorGroups} updateStatus={updateEventStatus} />}

      {tab === 'performance' && <Performance />}

      {tab === 'security' && <Security groups={secGroups} events={securityEvents} />}
    </AdminLayout>
  );
}

// ---------- Sub components ----------

function StatCard({ label, value, tone = 'navy', sub }) {
  const tones = {
    navy: '#0A1929',
    red: '#DC2626',
    amber: '#D97706',
    green: '#16A34A'
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <div className="text-xs text-gray-400 font-medium">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: tones[tone] }}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function Overview({ events, errorEvents, securityEvents, openCount, maintenance, health }) {
  const uptime = formatUptime(health?.uptime_seconds);
  const critical = events.filter(e => e.severity === 'critical').length;

  return (
    <div>
      <div className="bg-navy rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <span className={`h-3 w-3 rounded-full ${openCount === 0 ? 'bg-green-400' : 'bg-red-400'} inline-block`} />
          {openCount === 0 ? 'All systems operational' : `${openCount} open issue${openCount > 1 ? 's' : ''} need attention`}
        </div>
        <p className="text-gray-300 text-sm mt-1">
          Uptime: {uptime} · Backend online · {maintenance ? '⚠️ Maintenance mode ON' : 'Maintenance mode off'}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Errors (24h)" value={errorEvents.length} tone={errorEvents.length > 0 ? 'red' : 'green'} />
        <StatCard label="Open Issues" value={openCount} tone={openCount > 0 ? 'red' : 'green'} />
        <StatCard label="Critical" value={critical} tone={critical > 0 ? 'red' : 'green'} />
        <StatCard label="Security Events (24h)" value={securityEvents.length} tone={securityEvents.length > 0 ? 'amber' : 'green'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <TrendLineChart data={health?.trend || []} />
        <HourlyBarChart data={health?.byHour || []} />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <h3 className="font-semibold text-navy mb-4">Recent system events</h3>
        {events.length === 0 ? (
          <p className="text-sm text-gray-400">No recent errors or security events. All clear.</p>
        ) : events.slice(0, 8).map((e, i) => (
          <div key={e.id || i} className="flex items-center justify-between py-2 border-b border-gray-50 text-sm">
            <span className="text-gray-600">
              <span className="text-xs font-semibold mr-2" style={{ color: (SEVERITY[e.severity] || SEVERITY.low).color }}>
                {EVENT_TYPES[e.event_type] || e.event_type}
              </span>
              {e.message}
            </span>
            <span className="text-xs text-gray-400">{fmtTime(e.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendLineChart({ data }) {
  const W = 340, H = 140, PAD = 10;
  const max = Math.max(1, ...data.map(d => d.errors + d.security));
  const stepX = data.length > 1 ? (W - PAD * 2) / (data.length - 1) : 0;

  const pt = (i, v) => [PAD + i * stepX, H - PAD - (v / max) * (H - PAD * 2)];
  const linePath = (key) => data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${pt(i, d[key])[0]} ${pt(i, d[key])[1]}`).join(' ');

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <h3 className="font-semibold text-navy mb-1">7-day trend</h3>
      <p className="text-xs text-gray-400 mb-3">Errors vs security events (per day)</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 140 }}>
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#E5E7EB" strokeWidth="1" />
        <path d={linePath('errors')} fill="none" stroke="#DC2626" strokeWidth="2" />
        <path d={linePath('security')} fill="none" stroke="#D97706" strokeWidth="2" />
        {data.map((d, i) => (
          <circle key={i} cx={pt(i, d.errors)[0]} cy={pt(i, d.errors)[1]} r="2.5" fill="#DC2626" />
        ))}
        {data.map((d, i) => {
          const [cx, cy] = pt(i, d.errors + d.security);
          return <circle key={`s${i}`} cx={cx} cy={cy} r="2.5" fill="#D97706" />;
        })}
      </svg>
      <div className="flex gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-1 bg-red-600 inline-block rounded" /> Errors</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1 bg-amber-600 inline-block rounded" /> Security</span>
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-gray-300">
        {data.map((d, i) => <span key={i}>{d.label}</span>)}
      </div>
    </div>
  );
}

function HourlyBarChart({ data }) {
  const W = 340, H = 120, PAD = 8;
  const max = Math.max(1, ...data.map(d => d.count));
  const bw = (W - PAD * 2) / data.length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
      <h3 className="font-semibold text-navy mb-1">Activity (24h)</h3>
      <p className="text-xs text-gray-400 mb-3">All events by hour</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 120 }}>
        {data.map((d, i) => {
          const h = (d.count / max) * (H - PAD * 2);
          const x = PAD + i * bw;
          const y = H - PAD - h;
          return <rect key={i} x={x + 1} y={y} width={Math.max(1, bw - 2)} height={h} rx="1" fill={d.count > 0 ? '#00A86B' : '#EEF2F7'} />;
        })}
      </svg>
      <div className="flex justify-between mt-1 text-[10px] text-gray-300">
        <span>24h ago</span><span>now</span>
      </div>
    </div>
  );
}

function Errors({ groups, updateStatus }) {
  if (groups.length === 0) {
    return <EmptyState text="No errors recorded in the last 24 hours." />;
  }
  return (
    <div className="space-y-2">
      {groups.map(g => {
        const sev = SEVERITY[g.severity] || SEVERITY.low;
        const st = STATUS[g.status] || STATUS.open;
        return (
          <div key={g.message} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ color: sev.color, backgroundColor: sev.bg }}>{sev.label}</span>
                  <span className="text-xs text-gray-400">{g.count}×</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                </div>
                <p className="text-sm text-navy font-medium">{g.message}</p>
                <p className="text-xs text-gray-400 mt-1">{g.endpoint} · user: {g.user_email || '—'} · last {fmtTime(g.last_seen)}</p>
              </div>
              <select
                value={g.status}
                onChange={e => updateStatus(g.id, e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600"
              >
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Performance() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm text-gray-500 text-sm">
      Performance analytics (slow endpoints + response time trends) will appear here once response-time capture is wired in. The system is currently logging errors and security events.
    </div>
  );
}

function Security({ groups, events }) {
  if (groups.length === 0) return <EmptyState text="No security events in the last 24 hours — all clear." />;
  return (
    <div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm mb-4">
        <h3 className="font-semibold text-navy mb-2">Threat summary</h3>
        <p className="text-sm text-gray-500">
          {events.length > 0 ? '⚠️ Security events detected in the last 24h. Review below.' : 'All clear.'}
        </p>
      </div>
      {groups.map(g => (
        <div key={g.event_type} className="bg-white rounded-2xl border border-gray-100 p-4 mb-2 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-navy">{EVENT_TYPES[g.event_type] || g.event_type}</span>
            <span className="text-sm text-gray-400">{g.count} events</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Last seen {fmtTime(g.last_seen)}</p>
        </div>
      ))}
      <p className="text-xs text-gray-400 mt-4">
        <strong>Incident response:</strong> detect → contain (maintenance mode) → investigate → remediate → resolve (mark in Errors tab).
      </p>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 shadow-sm">
      <FaCheckCircle className="mx-auto text-3xl text-green-500 mb-3" />
      {text}
    </div>
  );
}

// ---- helpers ----

function groupBy(list, keyFn) {
  const map = {};
  for (const item of list) {
    const k = typeof keyFn === 'function' ? keyFn(item) : item[keyFn];
    if (!map[k]) map[k] = { ...item, key: k, count: 0, id: item.id, last_seen: item.created_at };
    map[k].count += 1;
    map[k].last_seen = item.created_at > map[k].last_seen ? item.created_at : map[k].last_seen;
    map[k].severity = item.severity;
    map[k].message = item.message || k;
    map[k].endpoint = item.endpoint;
    map[k].user_email = item.user_email;
    map[k].event_type = item.event_type;
    map[k].status = item.status;
  }
  return Object.values(map);
}

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default SystemManagement;